import React, { useState } from 'react';
import { Box, Container, Typography, Grid, Paper, InputBase, Button } from '@mui/material';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BookDemoModal from '../components/BookDemoModal';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ForumIcon from '@mui/icons-material/Forum';

import testimonialDoctorImg from '../assets/testimonial_doctor.jpg';

const TEAL = '#0B6B6B';
const TEAL_DARK = '#094F4F';
const GREEN = '#25D366';
const NAVY = '#1A2F3A';

export default function ContactPage() {
  const [demoOpen, setDemoOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', practice: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) return;
    setSubmitted(true);
    setFormData({ name: '', email: '', practice: '', message: '' });
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
      <Navbar />

      {/* Hero Banner Section */}
      <Box 
        sx={{ 
          position: 'relative',
          backgroundColor: NAVY,
          color: '#ffffff',
          overflow: 'hidden',
          minHeight: { xs: 'auto', md: '500px' }
        }}
      >
        {/* Desktop absolute background image on the right */}
        <Box
          component="img"
          src={testimonialDoctorImg}
          alt="Contact Support Team"
          sx={{
            display: { xs: 'none', md: 'block' },
            position: 'absolute',
            top: 0,
            right: 0,
            height: '100%',
            width: '52%',
            objectFit: 'cover',
            objectPosition: 'center top',
            zIndex: 0,
            maskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.15) 15%, black 35%)',
            WebkitMaskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.15) 15%, black 35%)',
          }}
        />

        {/* Content Container */}
        <Container maxWidth="xl" sx={{ px: { xs: 3, md: 8 }, pt: { xs: 16, md: 22 }, pb: { xs: 8, md: 12 }, position: 'relative', zIndex: 1 }}>
          <Grid container>
            <Grid size={{ xs: 12, md: 6.5 }}>
              {/* Forum badge */}
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, backgroundColor: 'rgba(37,211,102,0.12)', border: '1px solid rgba(37,211,102,0.25)', borderRadius: '50px', px: 2, py: 0.7, mb: 3.5, width: 'fit-content' }}>
                <ForumIcon sx={{ color: GREEN, fontSize: 18 }} />
                <Typography sx={{ fontFamily: 'Plus Jakarta Sans', fontWeight: 700, fontSize: '12px', color: 'rgba(255,255,255,0.85)' }}>Support & Inquiries</Typography>
              </Box>

              <Typography sx={{ fontFamily: 'Outfit, sans-serif', fontSize: { xs: '38px', md: '56px' }, fontWeight: 800, lineHeight: 1.1, mb: 3, letterSpacing: '-1px' }}>
                Get in Touch with Us.
              </Typography>
              <Typography sx={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '16px', color: 'rgba(255,255,255,0.65)', mb: 4.5, lineHeight: 1.7, maxWidth: '540px' }}>
                Have questions about Care Beyond? Want to request a custom deployment demo for your clinic network? Send us a message and we'll reply shortly.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Main Content Area */}
      <Container maxWidth="xl" sx={{ py: 12, px: { xs: 3, md: 8 }, flexGrow: 1 }}>
        <Grid container spacing={6}>
          {/* Left Column: Contact details */}
          <Grid size={{ xs: 12, md: 5 }} sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Typography sx={{ fontFamily: 'Outfit, sans-serif', fontSize: { xs: '26px', md: '32px' }, fontWeight: 800, color: NAVY }}>
              Contact Information
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
              {[
                { icon: <PhoneIcon sx={{ color: TEAL }} />, title: 'Call Us', value: '+91 9069437777' },
                { icon: <EmailIcon sx={{ color: TEAL }} />, title: 'Email Support', value: 'carebeyond@gmail.com' },
                { icon: <LocationOnIcon sx={{ color: TEAL }} />, title: 'Visit Office', value: 'Fairmont 603, Aditya Empress Towers,\nShaikpet, Hyderabad - 500008,\nTelangana, India' }
              ].map((item, idx) => (
                <Box key={idx} sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 44, borderRadius: '8px', backgroundColor: 'rgba(11,107,107,0.06)', flexShrink: 0 }}>
                    {item.icon}
                  </Box>
                  <Box>
                    <Typography sx={{ fontFamily: 'Outfit, sans-serif', fontSize: '15px', fontWeight: 700, color: NAVY, mb: 0.5 }}>
                      {item.title}
                    </Typography>
                    <Typography sx={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '14px', color: '#64748b', lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                      {item.value}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Grid>

          {/* Right Column: Contact form box */}
          <Grid size={{ xs: 12, md: 7 }}>
            <Paper sx={{ p: { xs: 4, md: 5 }, borderRadius: '24px', border: '1px solid #e2e8f0', boxShadow: 'none', backgroundColor: '#ffffff' }}>
              <Typography sx={{ fontFamily: 'Outfit, sans-serif', fontSize: '22px', fontWeight: 800, color: NAVY, mb: 4 }}>
                Send Us a Message
              </Typography>

              {submitted ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 6 }}>
                  <CheckCircleIcon sx={{ color: '#10b981', fontSize: 60 }} />
                  <Typography sx={{ fontFamily: 'Outfit, sans-serif', fontSize: '20px', fontWeight: 700, color: NAVY }}>
                    Thank you!
                  </Typography>
                  <Typography sx={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '15px', color: '#64748b', textAlign: 'center' }}>
                    Your message has been sent successfully. One of our support managers will reach out to you shortly.
                  </Typography>
                </Box>
              ) : (
                <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
                  <Grid container spacing={2.5}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography sx={{ fontFamily: 'Outfit, sans-serif', fontSize: '13px', fontWeight: 700, color: NAVY, mb: 1 }}>
                        Full Name *
                      </Typography>
                      <Box sx={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', px: 2, py: 1.25 }}>
                        <InputBase 
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Dr. Rajesh Koothrapali"
                          sx={{ fontSize: '14px', fontFamily: 'Plus Jakarta Sans, sans-serif', width: '100%', color: '#0f172a' }}
                        />
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography sx={{ fontFamily: 'Outfit, sans-serif', fontSize: '13px', fontWeight: 700, color: NAVY, mb: 1 }}>
                        Email Address *
                      </Typography>
                      <Box sx={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', px: 2, py: 1.25 }}>
                        <InputBase 
                          required
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="rajesh@koothrapali.clinic"
                          sx={{ fontSize: '14px', fontFamily: 'Plus Jakarta Sans, sans-serif', width: '100%', color: '#0f172a' }}
                        />
                      </Box>
                    </Grid>
                  </Grid>

                  <Box>
                    <Typography sx={{ fontFamily: 'Outfit, sans-serif', fontSize: '13px', fontWeight: 700, color: NAVY, mb: 1 }}>
                      Practice/Clinic Name
                    </Typography>
                    <Box sx={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', px: 2, py: 1.25 }}>
                      <InputBase 
                        value={formData.practice}
                        onChange={(e) => setFormData({ ...formData, practice: e.target.value })}
                        placeholder="Koothrapali Cardiology Clinic"
                        sx={{ fontSize: '14px', fontFamily: 'Plus Jakarta Sans, sans-serif', width: '100%', color: '#0f172a' }}
                      />
                    </Box>
                  </Box>

                  <Box>
                    <Typography sx={{ fontFamily: 'Outfit, sans-serif', fontSize: '13px', fontWeight: 700, color: NAVY, mb: 1 }}>
                      Message *
                    </Typography>
                    <Box sx={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', px: 2, py: 1.25 }}>
                      <InputBase 
                        required
                        multiline
                        rows={4}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        placeholder="Tell us about your practice and how we can support you..."
                        sx={{ fontSize: '14px', fontFamily: 'Plus Jakarta Sans, sans-serif', width: '100%', color: '#0f172a' }}
                      />
                    </Box>
                  </Box>

                  <Button 
                    type="submit"
                    variant="contained"
                    sx={{
                      backgroundColor: TEAL,
                      color: '#ffffff',
                      textTransform: 'none',
                      fontFamily: 'Plus Jakarta Sans, sans-serif',
                      fontWeight: 700,
                      fontSize: '15px',
                      borderRadius: '10px',
                      py: 1.6,
                      boxShadow: '0 8px 28px rgba(11,107,107,0.4)',
                      '&:hover': { backgroundColor: TEAL_DARK, transform: 'translateY(-1px)', boxShadow: '0 12px 32px rgba(11,107,107,0.5)' },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    Submit Message
                  </Button>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <Footer onOpenDemo={() => setDemoOpen(true)} />
      <BookDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </Box>
  );
}
